import {
  Checkbox,
  DataTable,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  INPUT_MONO,
  LABEL,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { WORKING_BOX } from "@/components/tools/chemistry/formula-view";
import { t } from "@/lib/i18n/interpolate";
import {
  type HessStep,
  hessContribution,
  hessTotal,
} from "@/lib/tool-kit/chem-reference/reaction-enthalpy";
import { parseMeasuredNumber } from "@/lib/tool-kit/math/number-input";

export interface StepState {
  id: number;
  delta: string;
  multiplier: string;
  reversed: boolean;
}

export interface HessStrings {
  stepsHeading: string;
  stepDelta: string;
  stepMultiplier: string;
  stepReverse: string;
  stepRemove: string;
  addStep: string;
  hessHint: string;
  hessEmpty: string;
  hessIncomplete: string;
  hessTotal: string;
  hessColumnStep: string;
  hessColumnDelta: string;
  hessColumnFactor: string;
  hessColumnContribution: string;
  hessFactorReversed: string;
  hessWorking: string;
  hessWorkLine: string;
  hessWorkTotal: string;
  hessUnit: string;
}

type Fmt = (value: number, options?: Intl.NumberFormatOptions) => string;

const NUMBER = { maximumFractionDigits: 2, useGrouping: false } as const;
export const MAX_STEPS = 10;

/** The steps whose ΔH and multiplier both read as numbers (blank multiplier means 1). */
export function readSteps(steps: readonly StepState[]): {
  numeric: (HessStep & { id: number })[];
  incomplete: number;
} {
  const numeric: (HessStep & { id: number })[] = [];
  let incomplete = 0;
  for (const step of steps) {
    const delta = parseMeasuredNumber(step.delta);
    const multiplier =
      step.multiplier.trim() === "" ? 1 : parseMeasuredNumber(step.multiplier);
    if (delta === null || multiplier === null || multiplier === 0) {
      incomplete += 1;
    } else {
      numeric.push({
        id: step.id,
        delta,
        multiplier: Math.abs(multiplier),
        reversed: step.reversed !== multiplier < 0,
      });
    }
  }
  return { numeric, incomplete };
}

export function HessPanel({
  steps,
  onChange,
  strings,
  fmt,
}: {
  steps: readonly StepState[];
  onChange: (steps: StepState[]) => void;
  strings: HessStrings;
  fmt: Fmt;
}) {
  const { numeric, incomplete } = readSteps(steps);
  const total = hessTotal(numeric);
  const update = (id: number, patch: Partial<StepState>) =>
    onChange(steps.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  const nextId = Math.max(0, ...steps.map((s) => s.id)) + 1;
  return (
    <div className="space-y-4">
      <p className="text-muted-foreground text-sm">{strings.hessHint}</p>
      <h3 className={WIDGET_H3}>{strings.stepsHeading}</h3>
      <ol className="space-y-3">
        {steps.map((step, index) => {
          const n = String(index + 1);
          return (
            <li
              className="grid items-end gap-3 rounded-lg border border-border bg-card p-3 sm:grid-cols-[2fr_1fr_auto_auto]"
              key={step.id}
            >
              <div className="space-y-1">
                <label className={LABEL} htmlFor={`er-step-${step.id}-delta`}>
                  {t(strings.stepDelta, { n })}
                </label>
                <input
                  className={INPUT_MONO}
                  id={`er-step-${step.id}-delta`}
                  inputMode="decimal"
                  onChange={(e) => update(step.id, { delta: e.target.value })}
                  value={step.delta}
                />
              </div>
              <div className="space-y-1">
                <label className={LABEL} htmlFor={`er-step-${step.id}-mult`}>
                  {t(strings.stepMultiplier, { n })}
                </label>
                <input
                  className={INPUT_MONO}
                  id={`er-step-${step.id}-mult`}
                  inputMode="decimal"
                  onChange={(e) =>
                    update(step.id, { multiplier: e.target.value })
                  }
                  value={step.multiplier}
                />
              </div>
              <Checkbox
                checked={step.reversed}
                label={t(strings.stepReverse, { n })}
                onChange={(reversed) => update(step.id, { reversed })}
              />
              <button
                className={BUTTON_SMALL}
                onClick={() => onChange(steps.filter((s) => s.id !== step.id))}
                type="button"
              >
                {t(strings.stepRemove, { n })}
              </button>
            </li>
          );
        })}
      </ol>
      <button
        className={BUTTON_SECONDARY}
        disabled={steps.length >= MAX_STEPS}
        onClick={() =>
          onChange([
            ...steps,
            { id: nextId, delta: "", multiplier: "1", reversed: false },
          ])
        }
        type="button"
      >
        {strings.addStep}
      </button>
      <HessResults
        fmt={fmt}
        incomplete={incomplete}
        numeric={numeric}
        strings={strings}
        total={total}
      />
    </div>
  );
}

function HessResults({
  numeric,
  incomplete,
  total,
  strings,
  fmt,
}: {
  numeric: readonly (HessStep & { id: number })[];
  incomplete: number;
  total: number;
  strings: HessStrings;
  fmt: Fmt;
}) {
  if (numeric.length === 0) {
    return <Notice>{strings.hessEmpty}</Notice>;
  }
  const rows = numeric.map((step, index) => [
    String(index + 1),
    fmt(step.delta, NUMBER),
    `${step.reversed ? `${strings.hessFactorReversed} ` : ""}× ${fmt(step.multiplier, NUMBER)}`,
    fmt(hessContribution(step), NUMBER),
  ]);
  const terms = numeric
    .map((step) => `(${fmt(hessContribution(step), NUMBER)})`)
    .join(" + ");
  return (
    <section aria-live="polite" className="space-y-4">
      <StatGrid>
        <StatCard
          emphasis
          label={strings.hessTotal}
          value={`${fmt(total, NUMBER)} ${strings.hessUnit}`}
        />
      </StatGrid>
      <DataTable
        head={[
          strings.hessColumnStep,
          strings.hessColumnDelta,
          strings.hessColumnFactor,
          strings.hessColumnContribution,
        ]}
        rows={rows}
      />
      <div className={WORKING_BOX}>
        <p className="font-medium font-sans text-foreground">
          {strings.hessWorking}
        </p>
        <p>{t(strings.hessWorkLine, { terms })}</p>
        <p>
          {t(strings.hessWorkTotal, {
            total: `${fmt(total, NUMBER)} ${strings.hessUnit}`,
          })}
        </p>
      </div>
      {incomplete > 0 ? <Notice>{strings.hessIncomplete}</Notice> : null}
    </section>
  );
}
